import classNames from 'classnames'
import { Clock as ClockIcon } from 'iconoir-react'
import { uniqBy } from 'lodash'
import moment from 'moment'
import { Fragment, ReactElement, useContext } from 'react'
import { EmptySearch } from '../shared/styled/EmptySearch'
import { DisplayProviderAvatar } from './DisplayProviderAvatar'
import { SchedulingWizardContext } from './SchedulingWizard'
import { LocationJson } from 'app/models/Location'

export function TimeSlot({ location, startDate }: { location: LocationJson; startDate: string }): ReactElement {
  const {
    apptType,
    daySlots,
    providers,
    selectedDaySlotIndex,
    selectedAvailability,
    selectedTimeSlot,
    setSelectedAvailability,
    setSelectedTimeSlot,
  } = useContext(SchedulingWizardContext)

  return (
    <div className="flex flex-col gap-y-0 overflow-hidden bg-white md:gap-y-6">
      <div className="rounded-lg bg-trn-100 p-4 md:hidden">
        <div className="text-heading-sm text-trn-900">{apptType?.alias}</div>
        <div className="text-sm font-medium text-neutral-500">
          {moment(startDate).add(selectedDaySlotIndex, `days`).format(`dddd, MMMM D`)}
        </div>
      </div>
      <div className="bg-nuetral-400 my-4 text-ui-xs uppercase md:my-0">
        {`${location.timezone} ${selectedTimeSlot ? `- (${selectedTimeSlot.time})` : ``}`}
      </div>
      <div className="flex grow flex-col gap-y-1 overflow-y-auto">
        {selectedDaySlotIndex >= 0 && daySlots?.at(selectedDaySlotIndex)?.timeSlots ? (
          daySlots?.at(selectedDaySlotIndex)?.timeSlots.map((s) => {
            let isSelected = selectedTimeSlot?.time === s.time
            return (
              <Fragment key={s.time}>
                <button
                  className={`rounded-lg px-4 py-3 text-left shadow-elevation-1 md:py-2 ${
                    isSelected ? `border-2 border-[#0067FD] text-neutral-900` : `border text-neutral-500`
                  } hover:bg-gray-100`}
                  style={{
                    backgroundColor: isSelected ? `rgba(0, 134, 255, 0.08)` : `#FFFFFF`,
                  }}
                  onClick={() => {
                    setSelectedTimeSlot(s)
                    setSelectedAvailability?.(undefined)
                  }}
                >
                  <span className="text-sm font-medium leading-[17.5px]">{s.time}</span>
                </button>
                {isSelected && (
                  <div className="flex flex-col gap-y-1.5 rounded-lg bg-neutral-100 p-4 text-sm font-medium">
                    <span className="mb-0.5 text-neutral-700">Booking with</span>
                    {uniqBy(selectedTimeSlot?.availabilities, `providerId`).map((availability, i, a) => {
                      let p = providers.find((p) => p.id === availability.providerId)
                      return (
                        p && (
                          <button
                            ref={(btn) => {
                              // auto select provider if only one available
                              if (a.length === 1 && btn) {
                                setSelectedAvailability?.(availability)
                              }
                              // scroll to selected provider button
                              if (selectedAvailability?.providerId === availability.providerId && btn) {
                                btn.scrollIntoView({ behavior: `smooth`, block: `center` })
                              }
                            }}
                            key={availability.id!}
                            className={classNames(
                              `flex items-center gap-x-2 rounded-lg p-2`,
                              selectedAvailability?.providerId === availability.providerId
                                ? `border-2 border-[#0067FD] text-neutral-900 shadow-elevation-1`
                                : `border border-black border-opacity-10 bg-white text-neutral-500`,
                            )}
                            onClick={() => setSelectedAvailability?.(availability)}
                            style={{
                              backgroundColor:
                                selectedAvailability?.providerId === availability.providerId
                                  ? `rgba(0, 134, 255, 0.08)`
                                  : `#FFFFFF`,
                            }}
                          >
                            <div className="h-5 w-5">
                              <DisplayProviderAvatar provider={p} />
                            </div>
                            <span className="text-left capitalize">{`${providers
                              .find((p) => p.id === availability.providerId)
                              ?.displayName?.toLowerCase()}`}</span>
                          </button>
                        )
                      )
                    })}
                  </div>
                )}
              </Fragment>
            )
          })
        ) : (
          <EmptySearch
            icon={() => <ClockIcon />}
            title="Select a Date"
            description="There are no available time slots for the selected date."
            className="h-full"
          />
        )}
      </div>
    </div>
  )
}
